/**
 * @description Estimate detail/view page for a single estimate
 *
 * Key features:
 * - Fetches estimate by number from URL params
 * - Header with status badge, action buttons, and total amount
 * - Left sidebar: customer billing details, internal notes, customer notes
 * - Main content: estimate info, line items table, totals section
 * - Status-dependent actions: edit, send, accept, decline, convert to invoice, delete
 * - Delete confirmation modal with soft-delete
 * - Convert to invoice confirmation modal with navigation to new invoice
 * - Conversion info display when status='converted' with link to invoice
 * - Expiration date display with warning styling when expired
 *
 * @dependencies
 * - estimateService: CRUD, status actions, conversion API calls
 * - estimateHelpers: formatCurrency, isEstimateExpired
 * - invoiceHelpers: formatInvoiceDate (reused for date display)
 * - StatusBadge: shared status badge component (type="estimate_status")
 * - PermissionGate: RBAC permission gating
 * - PERMISSIONS: permission code mappings
 *
 * @notes
 * - Mirrors InvoiceDetails.js structure with estimate-specific differences
 * - No payment section (no Record Payment, Payment History, Balance Due, Amount Paid)
 * - No void action; PDF preview uses shared PdfPreviewModal component
 * - StatusBadge type="estimate_status" expects a simple string value (not object)
 */

import { useState, useEffect, useRef, Fragment } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import estimateService from '../../services/api/estimateService';
import Spinner from '../shared/Spinner';
import {
  formatCurrency,
  isEstimateExpired,
  itemsFromEstimateResponse
} from '../../utils/estimateHelpers';
import { formatInvoiceDate, formatTimestampDate, namedDiscountLines } from '../../utils/invoiceHelpers';
import StatusBadge from '../shared/StatusBadge';
import PermissionGate from '../shared/PermissionGate';
import { PERMISSIONS } from '../../constants/permissionMappings';
import SendEstimateModal from './SendEstimateModal';
import PdfPreviewModal from '../invoices/PdfPreviewModal';
function EstimateDetails() {
  const { estimateNumber } = useParams();
  const navigate = useNavigate();

  // Core state
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [actionError, setActionError] = useState('');

  // Notes editing state
  const [internalNotes, setInternalNotes] = useState('');
  const [editingNotes, setEditingNotes] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);

  // Modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [processingAction, setProcessingAction] = useState(false);

  // Timer refs for cleanup on unmount
  const timerRefs = useRef([]);

  useEffect(() => {
    const timers = timerRefs.current;
    return () => {
      timers.forEach(clearTimeout);
    };
  }, []);

  const setManagedTimeout = (callback, delay) => {
    const id = setTimeout(callback, delay);
    timerRefs.current.push(id);
    return id;
  };

  // Note: Escape key handling is provided by headlessui Dialog (focus trap + ESC built-in)

  // Fetch estimate data
  useEffect(() => {
    if (!estimateNumber || estimateNumber === 'undefined') {
      if (estimateNumber === 'undefined') {
        setError('Invalid estimate number. Please navigate from the estimate list.');
        setLoading(false);
      }
      return;
    }

    const fetchEstimate = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await estimateService.getEstimate(estimateNumber);
        const est = data.success ? data.estimate : null;
        const items = itemsFromEstimateResponse(data);
        if (est) {
          est.subtotal = parseFloat(est.subtotal) || 0;
          est.taxableSubtotal = parseFloat(est.taxableSubtotal) || 0;
          est.taxAmount = parseFloat(est.taxAmount) || 0;
          est.discountAmount = parseFloat(est.discountAmount) || 0;
          est.shippingCost = parseFloat(est.shippingCost) || 0;
          est.totalAmount = parseFloat(est.totalAmount) || 0;
          est.items = (items || []).map(item => ({
            ...item,
            unitPrice: parseFloat(item.unitPrice) || 0,
            total: parseFloat(item.total) || 0
          }));
          setEstimate(est);
          setInternalNotes(est.internalNotes || '');
        } else {
          setError('Estimate data not found');
        }
      } catch (err) {
        // Error handled via UI state
        setError('Failed to load estimate');
      } finally {
        setLoading(false);
      }
    };

    fetchEstimate();
  }, [estimateNumber]);

  // Refetch estimate data (used after status actions)
  const refetchEstimate = async () => {
    if (!estimateNumber || estimateNumber === 'undefined') return;
    try {
      const data = await estimateService.getEstimate(estimateNumber);
      const est = data.success ? data.estimate : null;
      const items = itemsFromEstimateResponse(data);
      if (est) {
        est.subtotal = parseFloat(est.subtotal) || 0;
        est.taxableSubtotal = parseFloat(est.taxableSubtotal) || 0;
        est.taxAmount = parseFloat(est.taxAmount) || 0;
        est.discountAmount = parseFloat(est.discountAmount) || 0;
        est.shippingCost = parseFloat(est.shippingCost) || 0;
        est.totalAmount = parseFloat(est.totalAmount) || 0;
        est.items = (items || []).map(item => ({
          ...item,
          unitPrice: parseFloat(item.unitPrice) || 0,
          total: parseFloat(item.total) || 0
        }));
        setEstimate(est);
        setInternalNotes(est.internalNotes || '');
      }
    } catch (err) {
      // Refetch failure is non-blocking — page stays on stale data
    }
  };

  // Action handlers
  const handleSend = () => {
    setShowSendModal(true);
  };

  const handleSendSuccess = () => {
    setShowSendModal(false);
    setSuccessMessage('Estimate sent successfully');
    refetchEstimate();
    setManagedTimeout(() => setSuccessMessage(''), 5000);
  };

  const handleAccept = async () => {
    try {
      setProcessingAction(true);
      setActionError('');
      await estimateService.acceptEstimate(estimateNumber);
      setSuccessMessage('Estimate marked as accepted');
      await refetchEstimate();
      setManagedTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      // Error handled via UI state
      const errorMessage = err.response?.data?.error || err.message || 'Failed to accept estimate';
      setActionError(errorMessage);
    } finally {
      setProcessingAction(false);
    }
  };

  const handleDecline = async () => {
    try {
      setProcessingAction(true);
      setActionError('');
      await estimateService.declineEstimate(estimateNumber, declineReason.trim() || null);
      setShowDeclineModal(false);
      setDeclineReason('');
      setSuccessMessage('Estimate marked as declined');
      await refetchEstimate();
      setManagedTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      // Error handled via UI state
      const errorMessage = err.response?.data?.error || err.message || 'Failed to decline estimate';
      setActionError(errorMessage);
      setShowDeclineModal(false);
    } finally {
      setProcessingAction(false);
    }
  };

  const handleConvert = async () => {
    try {
      setProcessingAction(true);
      setActionError('');
      const result = await estimateService.convertToInvoice(estimateNumber);
      setShowConvertModal(false);
      if (result?.invoice?.invoiceNumber) {
        const invoiceNumber = result.invoice.invoiceNumber;
        setSuccessMessage(`Estimate converted to Invoice #${invoiceNumber}`);
        setManagedTimeout(() => {
          navigate(`/merchant/invoices/${invoiceNumber}`);
        }, 1500);
      } else {
        setSuccessMessage('Estimate converted to invoice successfully');
        await refetchEstimate();
        setManagedTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err) {
      // Error handled via UI state
      const errorMessage = err.response?.data?.error || err.message || 'Failed to convert estimate to invoice';
      setActionError(errorMessage);
      setShowConvertModal(false);
    } finally {
      setProcessingAction(false);
    }
  };

  const handleDelete = async () => {
    try {
      setProcessingAction(true);
      setActionError('');
      await estimateService.deleteEstimate(estimateNumber);
      setShowDeleteModal(false);
      setSuccessMessage('Estimate deleted successfully');
      setManagedTimeout(() => {
        navigate('/merchant/estimates');
      }, 1500);
    } catch (err) {
      // Error handled via UI state
      const errorMessage = err.response?.data?.error || err.message || 'Failed to delete estimate';
      setActionError(errorMessage);
      setShowDeleteModal(false);
    } finally {
      setProcessingAction(false);
    }
  };

  const handleEdit = () => {
    navigate(`/merchant/estimates/${estimateNumber}/edit`);
  };

  const handleDuplicate = async () => {
    try {
      setProcessingAction(true);
      setActionError('');
      const result = await estimateService.cloneEstimate(estimateNumber);
      if (result?.estimate?.estimateNumber) {
        setSuccessMessage(`Estimate duplicated as #${result.estimate.estimateNumber}`);
        setManagedTimeout(() => {
          navigate(`/merchant/estimates/${result.estimate.estimateNumber}`);
        }, 1500);
      } else {
        setSuccessMessage('Estimate duplicated successfully');
        setManagedTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to duplicate estimate';
      setActionError(errorMessage);
    } finally {
      setProcessingAction(false);
    }
  };

  const handleSaveNotes = async () => {
    try {
      setSavingNotes(true);
      await estimateService.updateEstimate(estimate.estimateNumber, { internalNotes: internalNotes });
      setEditingNotes(false);
      setEstimate(prev => ({ ...prev, internalNotes: internalNotes }));
    } catch (err) {
      // Error handled via UI state
      alert('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  // Show nothing while params are loading
  if (!estimateNumber) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-10">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
      </div>
    );
  }

  if (error || !estimate) {
    return (
      <div className="max-w-7xl mx-auto px-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error || 'Estimate not found'}
        </div>
      </div>
    );
  }

  // Derived state
  const expired = isEstimateExpired(estimate.status, estimate.expirationDate);
  const displayStatus = expired ? 'expired' : estimate.status;
  const isConverted = estimate.status === 'converted';
  const isDraft = estimate.status === 'draft';
  const isSent = estimate.status === 'sent' && !expired;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          Back to Estimates
        </button>

        <div className="flex items-center gap-4">
          <StatusBadge
            type="estimate_status"
            value={displayStatus}
            size="sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left Sidebar */}
        <div className="lg:col-span-1 space-y-6">
          {/* Billing Details Card */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-5">Billing Details</h3>

            <div className="space-y-5">
              {/* Customer Name */}
              <div className="flex items-start gap-4">
                <div className="w-9 h-9 bg-gray-50 rounded-full flex items-center justify-center border border-gray-100 flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 break-words">
                    {estimate.company || `${estimate.firstName || ''} ${estimate.lastName || ''}`.trim()}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5 uppercase tracking-wider font-medium">Customer</p>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start gap-4">
                <div className="w-9 h-9 bg-gray-50 rounded-full flex items-center justify-center border border-gray-100 flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 break-all">{estimate.email}</p>
                  <p className="text-xs text-gray-500 mt-0.5 uppercase tracking-wider font-medium">Email Address</p>
                </div>
              </div>

              {/* Phone */}
              {estimate.phone && (
                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 bg-gray-50 rounded-full flex items-center justify-center border border-gray-100 flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{estimate.phone}</p>
                    <p className="text-xs text-gray-500 mt-0.5 uppercase tracking-wider font-medium">Phone</p>
                  </div>
                </div>
              )}

              {/* Address */}
              <div className="flex items-start gap-4">
                <div className="w-9 h-9 bg-gray-50 rounded-full flex items-center justify-center border border-gray-100 flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  {(estimate.address || estimate.city || estimate.state || estimate.zip) ? (
                    <p className="text-sm font-medium text-gray-900 break-words leading-relaxed">
                      {estimate.address && <>{estimate.address}<br /></>}
                      {estimate.city}{estimate.city && estimate.state ? ', ' : ''}{estimate.state} {estimate.zip}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-400 italic">No customer billing address on file</p>
                  )}
                  <p className="text-xs text-gray-500 mt-0.5 uppercase tracking-wider font-medium">Customer Billing Address</p>
                </div>
              </div>
            </div>
          </div>

          {/* Notes Card - Desktop Only */}
          <div className="hidden lg:block bg-gray-50 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">
                Notes <span className="text-sm font-normal text-gray-500">(Internal)</span>
              </h3>
              {!editingNotes && isDraft && (
                <button
                  onClick={() => setEditingNotes(true)}
                  className="text-sm text-brand hover:text-brand-hover font-medium"
                >
                  Edit
                </button>
              )}
            </div>

            {editingNotes ? (
              <div>
                <textarea
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg text-sm"
                  rows="6"
                  placeholder="Add internal notes..."
                />
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={handleSaveNotes}
                    disabled={savingNotes}
                    className="px-4 py-2 bg-brand text-white rounded-lg text-sm font-medium hover:bg-brand-hover disabled:opacity-50"
                  >
                    {savingNotes ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => {
                      setInternalNotes(estimate.internalNotes || '');
                      setEditingNotes(false);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-600 whitespace-pre-wrap">
                {internalNotes || 'No notes added yet.'}
              </p>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3 space-y-6">
          {/* Estimate Header Info */}
          <div className="bg-gray-50 rounded-xl p-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold mb-2">Estimate #{estimate.estimateNumber}</h2>
                <div className="space-y-1">
                  <p className="text-gray-600">Issued {formatInvoiceDate(estimate.issueDate || estimate.estimateDate)}</p>
                  {estimate.expirationDate && (
                    <p className={`text-sm ${expired ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                      {expired ? (
                        <>
                          <svg className="w-4 h-4 inline mr-1 -mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                          </svg>
                          Expired {formatInvoiceDate(estimate.expirationDate)}
                        </>
                      ) : (
                        <>Expires {formatInvoiceDate(estimate.expirationDate)}</>
                      )}
                    </p>
                  )}
                  {estimate.paymentTerms && (
                    <p className="text-sm text-gray-500">Terms: {estimate.paymentTerms.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                  )}
                  {estimate.poNumber && (
                    <p className="text-sm text-gray-500">PO #{estimate.poNumber}</p>
                  )}
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600 mb-1">Created: {formatTimestampDate(estimate.createdAt)}</p>
                <p className="text-3xl font-bold">{formatCurrency(estimate.totalAmount || 0)}</p>
              </div>
            </div>

            {/* Conversion Info */}
            {isConverted && estimate.convertedInvoiceNumber && (
              <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-purple-800">
                    Converted to{' '}
                    <Link
                      to={`/merchant/invoices/${estimate.convertedInvoiceNumber}`}
                      className="font-semibold text-purple-700 hover:text-purple-900 underline"
                    >
                      Invoice #{estimate.convertedInvoiceNumber}
                    </Link>
                    {estimate.convertedAt && (
                      <span className="text-purple-600"> on {formatInvoiceDate(estimate.convertedAt)}</span>
                    )}
                  </p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              {/* Draft actions */}
              {isDraft && (
                <>
                  <PermissionGate permission={PERMISSIONS.estimates.send}>
                    <button
                      onClick={handleSend}
                      disabled={processingAction}
                      className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 font-medium disabled:opacity-50"
                    >
                      {processingAction ? (
                        <span className="flex items-center gap-2">
                          <Spinner />
                          Sending...
                        </span>
                      ) : 'Send Estimate'}
                    </button>
                  </PermissionGate>
                  <PermissionGate permission={PERMISSIONS.estimates.edit}>
                    <button
                      onClick={handleEdit}
                      className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
                    >
                      Edit Estimate
                    </button>
                  </PermissionGate>
                  <PermissionGate permission={PERMISSIONS.estimates.delete}>
                    <button
                      onClick={() => setShowDeleteModal(true)}
                      className="px-4 py-2 bg-white border border-red-300 text-red-600 rounded-lg hover:bg-red-50 font-medium"
                    >
                      Delete
                    </button>
                  </PermissionGate>
                </>
              )}

              {/* Sent actions (not expired) */}
              {isSent && (
                <>
                  <PermissionGate permission={PERMISSIONS.estimates.edit}>
                    <button
                      onClick={handleAccept}
                      disabled={processingAction}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50"
                    >
                      {processingAction ? (
                        <span className="flex items-center gap-2">
                          <Spinner />
                          Processing...
                        </span>
                      ) : 'Mark Accepted'}
                    </button>
                  </PermissionGate>
                  <PermissionGate permission={PERMISSIONS.estimates.edit}>
                    <button
                      onClick={() => setShowDeclineModal(true)}
                      disabled={processingAction}
                      className="px-4 py-2 bg-white border border-red-300 text-red-600 rounded-lg hover:bg-red-50 font-medium disabled:opacity-50"
                    >
                      Mark Declined
                    </button>
                  </PermissionGate>
                  <PermissionGate permission={PERMISSIONS.estimates.convert}>
                    <button
                      onClick={() => setShowConvertModal(true)}
                      disabled={processingAction}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium disabled:opacity-50"
                    >
                      Convert to Invoice
                    </button>
                  </PermissionGate>
                  <PermissionGate permission={PERMISSIONS.estimates.send}>
                    <button
                      onClick={handleSend}
                      disabled={processingAction}
                      className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium disabled:opacity-50"
                    >
                      {processingAction ? (
                        <span className="flex items-center gap-2">
                          <Spinner />
                          Sending...
                        </span>
                      ) : 'Resend'}
                    </button>
                  </PermissionGate>
                </>
              )}

              {/* Accepted actions */}
              {estimate.status === 'accepted' && (
                <PermissionGate permission={PERMISSIONS.estimates.convert}>
                  <button
                    onClick={() => setShowConvertModal(true)}
                    disabled={processingAction}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium disabled:opacity-50"
                  >
                    Convert to Invoice
                  </button>
                </PermissionGate>
              )}

              {/* Declined / Expired actions */}
              {(estimate.status === 'declined' || estimate.status === 'expired' || (expired && estimate.status === 'sent')) && (
                <PermissionGate permission={PERMISSIONS.estimates.delete}>
                  <button
                    onClick={() => setShowDeleteModal(true)}
                    className="px-4 py-2 bg-white border border-red-300 text-red-600 rounded-lg hover:bg-red-50 font-medium"
                  >
                    Delete
                  </button>
                </PermissionGate>
              )}

              {/* Converted actions */}
              {isConverted && estimate.convertedInvoiceNumber && (
                <Link
                  to={`/merchant/invoices/${estimate.convertedInvoiceNumber}`}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium inline-flex items-center gap-2"
                >
                  View Invoice
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </Link>
              )}

              {/* Duplicate (visible for non-draft statuses) */}
              {!isDraft && (
                <PermissionGate permission={PERMISSIONS.estimates.create}>
                  <button
                    onClick={handleDuplicate}
                    disabled={processingAction}
                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium disabled:opacity-50"
                  >
                    Duplicate
                  </button>
                </PermissionGate>
              )}

              {/* View Estimate PDF (always available) */}
              <button
                onClick={() => setShowPdfPreview(true)}
                disabled={processingAction}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium disabled:opacity-50"
              >
                View Estimate PDF
              </button>
            </div>
          </div>

          {/* Line Items */}
          <div className="bg-gray-50 rounded-xl overflow-hidden">
            <div className="px-6 py-4 bg-gray-50">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-lg">Line Items</h3>
              </div>
            </div>

            <div className="overflow-x-auto bg-gray-50 px-6">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product/Service</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item Details</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">QTY</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Amount</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Tax</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {estimate.items?.map((item, index) => (
                    <tr key={item.id || index}>
                      <td className="px-6 py-4">
                        <p className="font-medium">{item.itemName || item.description || 'Product Name'}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-600">{item.description || 'Information about this item'}</p>
                      </td>
                      <td className="px-6 py-4 text-center">{item.quantity}</td>
                      <td className="px-6 py-4 text-right">{formatCurrency(item.unitPrice || 0)}</td>
                      <td className="px-6 py-4 text-right font-medium">{formatCurrency(item.totalPrice || 0)}</td>
                      <td className="px-6 py-4 text-center">
                        {item.isTaxable && (
                          <svg className="w-5 h-5 text-green-600 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Bottom Section with Notes and Totals */}
            <div className="p-6 bg-gray-50 border-t border-gray-300">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Side - Notes */}
                <div className="space-y-6">
                  {/* Customer Notes */}
                  {estimate.customerNotes && (
                    <div>
                      <h3 className="font-semibold mb-3">Notes to Customer</h3>
                      <p className="text-gray-700 whitespace-pre-wrap">
                        {estimate.customerNotes}
                      </p>
                    </div>
                  )}

                  {/* Terms */}
                  {estimate.terms && (
                    <div>
                      <h3 className="font-semibold mb-3">Terms &amp; Conditions</h3>
                      <p className="text-gray-700 whitespace-pre-wrap">
                        {estimate.terms}
                      </p>
                    </div>
                  )}

                  {/* Footer */}
                  {estimate.footer && (
                    <div>
                      <h3 className="font-semibold mb-3">Footer</h3>
                      <p className="text-gray-700 whitespace-pre-wrap">
                        {estimate.footer}
                      </p>
                    </div>
                  )}
                </div>

                {/* Right Side - Totals */}
                <div className="lg:text-right">
                  <div className="inline-block text-left">
                    <div className="space-y-2">
                      <div className="flex justify-between gap-16">
                        <span className="text-gray-600">Subtotal:</span>
                        <span className="font-medium">{formatCurrency(estimate.subtotal || 0)}</span>
                      </div>

                      <div className="flex justify-between gap-16">
                        <span className="text-gray-600">Taxable Subtotal:</span>
                        <span className="font-medium">{formatCurrency(estimate.taxableSubtotal || 0)}</span>
                      </div>

                      <div className="flex justify-between gap-16">
                        <span className="text-gray-600">Sales Tax:</span>
                        <span className="font-medium">{formatCurrency(estimate.taxAmount || 0)}</span>
                      </div>

                      <div className="flex justify-between gap-16">
                        <span className="text-gray-600">Shipping:</span>
                        <span className="font-medium">{formatCurrency(estimate.shippingCost || 0)}</span>
                      </div>

                      {namedDiscountLines(estimate.appliedDiscounts, estimate.discountAmount).map((row, index) => (
                        <div key={`named-discount-${index}`} className="flex justify-between gap-16">
                          <span className="text-gray-600">{row.title}:</span>
                          <span className="font-medium text-red-600">-{formatCurrency(row.amount)}</span>
                        </div>
                      ))}

                      <div className="pt-2 border-t">
                        <div className="flex justify-between gap-16 text-lg font-semibold">
                          <span>Total:</span>
                          <span>{formatCurrency(estimate.totalAmount || 0)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notes Card - Mobile/Tablet Only */}
          <div className="lg:hidden bg-gray-50 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">
                Notes <span className="text-sm font-normal text-gray-500">(Internal)</span>
              </h3>
              {!editingNotes && isDraft && (
                <button
                  onClick={() => setEditingNotes(true)}
                  className="text-sm text-brand hover:text-brand-hover font-medium"
                >
                  Edit
                </button>
              )}
            </div>

            {editingNotes ? (
              <div>
                <textarea
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg text-sm"
                  rows="6"
                  placeholder="Add internal notes..."
                />
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={handleSaveNotes}
                    disabled={savingNotes}
                    className="px-4 py-2 bg-brand text-white rounded-lg text-sm font-medium hover:bg-brand-hover disabled:opacity-50"
                  >
                    {savingNotes ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => {
                      setInternalNotes(estimate.internalNotes || '');
                      setEditingNotes(false);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-600 whitespace-pre-wrap">
                {internalNotes || 'No notes added yet.'}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mt-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
          {successMessage}
        </div>
      )}

      {/* Error Message */}
      {actionError && (
        <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded flex justify-between items-center">
          <span>{actionError}</span>
          <button
            onClick={() => setActionError('')}
            className="text-red-700 hover:text-red-900"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Transition.Root show={showDeleteModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => !processingAction && setShowDeleteModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
            leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300" enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform rounded-2xl bg-white shadow-xl transition-all w-full max-w-md">
                  <div className="px-6 py-4 bg-gray-50 rounded-t-2xl border-b border-gray-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                      </div>
                      <Dialog.Title className="text-lg font-semibold text-gray-900">Delete Estimate</Dialog.Title>
                    </div>
                  </div>

                  <div className="px-6 py-5">
                    <p className="text-gray-600">
                      Are you sure you want to delete estimate <span className="font-semibold">#{estimate?.estimateNumber}</span>? This action cannot be undone.
                    </p>
                  </div>

                  <div className="px-6 py-4 bg-gray-50 rounded-b-2xl border-t border-gray-200 flex justify-end gap-3">
                    <button
                      onClick={() => setShowDeleteModal(false)}
                      disabled={processingAction}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 font-medium text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={processingAction}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium text-sm disabled:opacity-50 inline-flex items-center gap-2"
                    >
                      {processingAction ? (
                        <>
                          <Spinner />
                          Deleting...
                        </>
                      ) : 'Delete Estimate'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Convert to Invoice Confirmation Modal */}
      <Transition.Root show={showConvertModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => !processingAction && setShowConvertModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
            leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300" enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform rounded-2xl bg-white shadow-xl transition-all w-full max-w-md">
                  <div className="px-6 py-4 bg-gray-50 rounded-t-2xl border-b border-gray-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <Dialog.Title className="text-lg font-semibold text-gray-900">Convert to Invoice</Dialog.Title>
                    </div>
                  </div>

                  <div className="px-6 py-5">
                    <p className="text-gray-600">
                      This will create a new invoice from this estimate and mark it as converted.
                      The estimate will become read-only. Continue?
                    </p>
                  </div>

                  <div className="px-6 py-4 bg-gray-50 rounded-b-2xl border-t border-gray-200 flex justify-end gap-3">
                    <button
                      onClick={() => setShowConvertModal(false)}
                      disabled={processingAction}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 font-medium text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleConvert}
                      disabled={processingAction}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium text-sm disabled:opacity-50 inline-flex items-center gap-2"
                    >
                      {processingAction ? (
                        <>
                          <Spinner />
                          Converting...
                        </>
                      ) : 'Convert to Invoice'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Decline Estimate Modal */}
      <Transition.Root show={showDeclineModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => !processingAction && setShowDeclineModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
            leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300" enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform rounded-2xl bg-white shadow-xl transition-all w-full max-w-md">
                  <div className="px-6 py-4 bg-gray-50 rounded-t-2xl border-b border-gray-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </div>
                      <Dialog.Title className="text-lg font-semibold text-gray-900">Decline Estimate</Dialog.Title>
                    </div>
                  </div>

                  <div className="px-6 py-5">
                    <p className="text-gray-600 mb-4">
                      Mark this estimate as declined. You can optionally provide a reason.
                    </p>
                    <textarea
                      value={declineReason}
                      onChange={(e) => setDeclineReason(e.target.value)}
                      placeholder="Reason for declining (optional)"
                      rows={3}
                      maxLength={500}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                    />
                  </div>

                  <div className="px-6 py-4 bg-gray-50 rounded-b-2xl border-t border-gray-200 flex justify-end gap-3">
                    <button
                      onClick={() => { setShowDeclineModal(false); setDeclineReason(''); }}
                      disabled={processingAction}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 font-medium text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDecline}
                      disabled={processingAction}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium text-sm disabled:opacity-50 inline-flex items-center gap-2"
                    >
                      {processingAction ? (
                        <>
                          <Spinner />
                          Declining...
                        </>
                      ) : 'Decline Estimate'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Send Estimate Email Modal */}
      <SendEstimateModal
        isOpen={showSendModal}
        onClose={() => setShowSendModal(false)}
        estimate={estimate}
        onSuccess={handleSendSuccess}
      />

      {/* PDF Preview Modal */}
      <PdfPreviewModal
        isOpen={showPdfPreview}
        onClose={() => setShowPdfPreview(false)}
        documentNumber={estimate?.estimateNumber}
        title={`Estimate PDF — ${estimate?.estimateNumber || ''}`}
        fetchPdfBlob={(num) => estimateService.downloadPdf(num)}
      />
    </div>
  );
}

export default EstimateDetails;
